/* ==========================================================================
   PILA — Release Notes section

   Custom element that fetches published entries from the Release Notes API
   and renders them grouped chronologically by Month/Year, mirroring Tesla's
   software-updates page. Category is a sidebar filter, not a grouping —
   picking one narrows the same chronological list rather than jumping to a
   section, same interaction shape as the Roadmap section's category filter.

   Each entry renders as a bold Version lead-in, then its body — written
   directly in the Notion page rather than a property, so real headings,
   paragraphs, bullets, and bold/italic all come through as typed instead of
   being guessed at from plain-text lines. The API pre-converts each block to
   {type, html}; this file just groups consecutive bullets into a <ul> and
   renders everything else as its own paragraph, in document order.
   Release Date is used only to bucket entries into their Month/Year group —
   no day-level date is shown per entry, same as Tesla's page. Title,
   Summary, Tags and Featured are no longer displayed (the API may still
   return them; they're just ignored here).

   Categories are section blocks (see the .liquid file): a block's "key"
   must match the Notion "Category" value exactly, same convention as the
   Roadmap section.
   ========================================================================== */

(function () {
  'use strict';

  if (!('customElements' in window)) return;

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  function monthYearLabel(value) {
    if (!value) return 'Undated';
    var parts = String(value).split('-');
    var year = Number(parts[0]);
    var month = Number(parts[1]);
    if (!year || !month) return 'Undated';
    return MONTHS[month - 1] + ' ' + year;
  }

  var ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

  // Every visible field comes from Notion via the API — escape anything that
  // reaches innerHTML so a stray "<" in a title or note can't open a tag.
  function esc(value) {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[&<>"']/g, function (ch) { return ESCAPES[ch]; });
  }

  class PilaReleaseNotes extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      var configEl = this.querySelector('[data-rn-config]');
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (err) {
        console.error('[pila-release-notes] bad config', err);
        return;
      }

      this.copy = this.config.copy || {};
      this.api = String(this.config.apiBase || '').replace(/\/+$/, '');
      this.categories = this.config.categories || [];
      this.activeCategory = 'all';
      this.searchQuery = '';
      this.items = [];

      this.stateEl = this.querySelector('[data-rn-state]');
      this.bodyEl = this.querySelector('[data-rn-body]');
      this.searchInput = this.querySelector('#pila-rn-search');
      this.navLinks = Array.from(this.querySelectorAll('.pila-rn__nav-item a'));

      this.setupNav();
      this.setupSearch();
      this.load();
    }

    async load() {
      try {
        var res = await fetch(this.api + '/api/release-notes');
        if (!res.ok) throw new Error('bad status ' + res.status);
        var data = await res.json();
        this.items = data.items || [];
        this.renderList();
      } catch (err) {
        console.error('[pila-release-notes] load failed', err);
        if (this.stateEl) {
          this.stateEl.textContent = this.copy.error || "Couldn't load release notes right now.";
          this.stateEl.style.display = 'block';
        }
      }
    }

    setupNav() {
      var self = this;
      this.navLinks.forEach(function (a) {
        a.addEventListener('click', function (e) {
          e.preventDefault();
          self.activeCategory = this.getAttribute('data-category');
          self.navLinks.forEach(function (link) {
            link.classList.toggle('active', link === this);
          }, this);
          self.renderList();
        });
      });
    }

    setupSearch() {
      if (!this.searchInput) return;
      var self = this;
      this.searchInput.addEventListener('input', function () {
        self.searchQuery = this.value.toLowerCase().trim();
        self.renderList();
      });
    }

    renderList() {
      if (!this.items.length) {
        if (this.stateEl) {
          this.stateEl.textContent = this.copy.empty || 'Nothing published yet.';
          this.stateEl.style.display = 'block';
        }
        this.bodyEl.innerHTML = '';
        return;
      }

      var self = this;
      var filtered = this.items.filter(function (item) {
        if (self.activeCategory !== 'all' && item.category !== self.activeCategory) return false;
        if (!self.searchQuery) return true;
        var bodyText = (item.body || []).map(function (b) { return b.html.replace(/<[^>]+>/g, ''); }).join(' ');
        var blob = [item.version, bodyText].join(' ').toLowerCase();
        return blob.indexOf(self.searchQuery) !== -1;
      });

      // Unrecognized categories are logged, not silently dropped, so a
      // typo'd Notion category is easy to spot rather than just vanishing.
      var configuredKeys = this.categories.map(function (c) { return c.key; });
      this.items.forEach(function (item) {
        if (configuredKeys.indexOf(item.category) === -1) {
          console.warn('[pila-release-notes] entry "' + item.title + '" has unrecognized category "' + item.category + '" — add a matching category block.');
        }
      });

      if (!filtered.length) {
        if (this.stateEl) {
          this.stateEl.textContent = this.copy.searchEmpty || 'No matches.';
          this.stateEl.style.display = 'block';
        }
        this.bodyEl.innerHTML = '';
        return;
      }

      if (this.stateEl) this.stateEl.style.display = 'none';

      // Items arrive newest-first from the API; bucket into month/year
      // groups in that same order rather than re-sorting.
      var groups = [];
      var groupIndex = {};
      filtered.forEach(function (item) {
        var key = monthYearLabel(item.date);
        if (!(key in groupIndex)) {
          groupIndex[key] = groups.length;
          groups.push({ label: key, items: [] });
        }
        groups[groupIndex[key]].items.push(item);
      });

      var html = groups.map(function (group) {
        var entries = group.items.map(function (item) { return self.renderEntry(item); }).join('');
        return (
          '<div class="pila-rn__group">' +
            '<h2 class="pila-rn__group-title">' + esc(group.label) + '</h2>' +
            entries +
          '</div>'
        );
      }).join('');

      this.bodyEl.innerHTML = html;
    }

    renderEntry(item) {
      return (
        '<article class="pila-rn__entry">' +
          (item.version ? '<div class="pila-rn__version">' + esc(item.version) + '</div>' : '') +
          this.renderBody(item.body || []) +
        '</article>'
      );
    }

    // item.body is an ordered list of {type: 'paragraph'|'heading'|'bullet',
    // html} segments, pre-escaped and pre-formatted server-side from the
    // Notion page's actual blocks. Consecutive bullets are grouped into one
    // <ul>; everything else becomes its own paragraph, in document order —
    // this is what keeps a heading a heading instead of turning into a
    // bullet point.
    renderBody(blocks) {
      var html = '';
      var listBuffer = [];

      function flushList() {
        if (listBuffer.length) {
          html += '<ul class="pila-rn__entry-notes">' + listBuffer.join('') + '</ul>';
          listBuffer = [];
        }
      }

      blocks.forEach(function (block) {
        if (block.type === 'bullet') {
          listBuffer.push('<li>' + block.html + '</li>');
          return;
        }
        flushList();
        var cls = block.type === 'heading' ? 'pila-rn__note-heading' : 'pila-rn__note-text';
        html += '<p class="' + cls + '">' + block.html + '</p>';
      });
      flushList();

      return html;
    }
  }

  customElements.define('pila-release-notes', PilaReleaseNotes);
})();
